/**
 * Errores HTTP devueltos por el cliente API (fetch).
 * Incluye el cuerpo JSON del backend para mapear `errors` de DRF.
 */
export class ApiRequestError extends Error {
  constructor(message, opts = {}) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = opts.status;
    this.body = opts.body || {};
  }
}

export function isApiRequestError(e) {
  return e instanceof ApiRequestError;
}

function stringifyDrfValue(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (Array.isArray(val)) {
    return val.map(stringifyDrfValue).filter(Boolean).join(' ');
  }
  if (typeof val === 'object') {
    return Object.entries(val)
      .map(([k, v]) => `${k}: ${stringifyDrfValue(v)}`)
      .filter(Boolean)
      .join(' ');
  }
  return String(val);
}

function coerceToStringList(val) {
  if (val == null) return [];
  if (Array.isArray(val)) return val.flatMap(coerceToStringList);
  if (typeof val === 'string') return [val];
  if (typeof val === 'object') return [stringifyDrfValue(val)].filter(Boolean);
  return [String(val)];
}

/**
 * Convierte `serializer.errors` de Django REST a mensajes por campo y generales.
 * @param {Record<string, unknown>|undefined|null} errors
 * @returns {{ fieldErrors: Record<string, string>, generalMessages: string[] }}
 */
export function drfErrorsToFormState(errors) {
  if (!errors || typeof errors !== 'object') {
    return { fieldErrors: {}, generalMessages: [] };
  }
  const fieldErrors = {};
  const generalMessages = [];

  for (const [key, raw] of Object.entries(errors)) {
    if (key === 'non_field_errors') {
      generalMessages.push(...coerceToStringList(raw));
      continue;
    }
    const text = stringifyDrfValue(raw);
    if (text) fieldErrors[key] = text;
  }
  return { fieldErrors, generalMessages };
}

export function primaryMessageFromApiBody(body, status) {
  if (body?.message) return String(body.message);
  const d = body?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.join(' ');
  return `HTTP ${status}`;
}

/**
 * Texto único para alertas (p. ej. borrado) cuando no hay formulario por campo.
 */
export function formatApiErrorForUser(error) {
  if (isApiRequestError(error)) {
    const { fieldErrors, generalMessages } = drfErrorsToFormState(error.body?.errors);
    const fieldLines = Object.entries(fieldErrors).map(([k, v]) => `${k}: ${v}`);
    const parts = [error.message, ...generalMessages, ...fieldLines].filter(Boolean);
    return [...new Set(parts)].join('\n');
  }
  return error?.message || 'Error desconocido';
}

/**
 * Mensaje de banner para formularios: mensaje principal + non_field_errors (sin repetir campos).
 */
export function buildFormErrorBanner(apiError) {
  if (!isApiRequestError(apiError)) {
    return apiError?.message ? String(apiError.message) : '';
  }
  const { generalMessages } = drfErrorsToFormState(apiError.body?.errors);
  const parts = [apiError.message, ...generalMessages].filter(Boolean);
  return [...new Set(parts)].join(' · ');
}

/**
 * Asigna errores DRF a campos de react-hook-form (solo claves permitidas).
 * @param {unknown} errors - `body.errors` del backend
 * @param {(field: string, err: { type: string; message: string }) => void} setError
 * @param {string[]} allowedFields
 */
export function applyApiErrorsToRhf(errors, setError, allowedFields) {
  const allow = new Set(allowedFields);
  const { fieldErrors } = drfErrorsToFormState(errors);
  for (const [key, text] of Object.entries(fieldErrors)) {
    if (allow.has(key)) {
      setError(key, { type: 'server', message: text });
    }
  }
}
